import { BundleCategoryKey } from 'components/dynamic-bundle/constants';
import COMMON_CATEGORIES from 'config/constants/common-categories';
import { PetType } from 'constants/pet-type';
import { BreedSegmentationIds } from 'constants/segmentation';
const { wetFood, dryFood, snacks } = COMMON_CATEGORIES;

export type BundleCompositionDictionary = {
  [key in BundleCategoryKey]: {
    minAmountPerCategory: number;
    amountPerItem: number;
  };
};

/**
 * initial dictionary determines of how many items in every category should be present
 * the order of items in the composition matters and affects parts of logic
 * used as default value inside of calculateBundle to getDictionary
 */
const BUNDLE_CAT_COMPOSITION_DICTIONARY: BundleCompositionDictionary = {
  [wetFood]: {
    minAmountPerCategory: 8,
    amountPerItem: 2,
  },
  [dryFood]: {
    minAmountPerCategory: 1,
    amountPerItem: 1,
  },
  [snacks]: {
    minAmountPerCategory: 5,
    amountPerItem: 1,
  },
};

const BUNDLE_DOG_COMPOSITION_DICTIONARY: BundleCompositionDictionary = {
  [wetFood]: {
    minAmountPerCategory: 4,
    amountPerItem: 2,
  },
  [dryFood]: {
    minAmountPerCategory: 1,
    amountPerItem: 1,
  },
  [snacks]: {
    minAmountPerCategory: 3,
    amountPerItem: 1,
  },
};

const PICKY_EATER_WET_FOOD_PRODUCTS = 5;

const SMALL_DOGS_BREEDS = [
  'chihuahua',
  'havanese',
  'dachshund',
  'spitz',
  'poodle',
  'mixed-dog-small',
  'terrier',
  'other-bree',
];

const DogBreedSize = {
  Small: 'small-breed',
  Medium: 'medium-breed',
  Large: 'large-breed',
} as const;

type DogBreedSize = (typeof DogBreedSize)[keyof typeof DogBreedSize];

export const BREED_SIZE_BY_BREED: Record<BreedSegmentationIds, DogBreedSize> = {
  [BreedSegmentationIds.Chihuahua]: DogBreedSize.Small,
  [BreedSegmentationIds.Dachshund]: DogBreedSize.Small,
  [BreedSegmentationIds.Havanese]: DogBreedSize.Small,
  [BreedSegmentationIds.MixedSmall]: DogBreedSize.Small,
  [BreedSegmentationIds.Spitz]: DogBreedSize.Small,
  [BreedSegmentationIds.Bulldog]: DogBreedSize.Medium,
  [BreedSegmentationIds.Herdingdog]: DogBreedSize.Medium,
  [BreedSegmentationIds.OtherBreed]: DogBreedSize.Medium,
  [BreedSegmentationIds.Poodle]: DogBreedSize.Medium,
  [BreedSegmentationIds.Terrier]: DogBreedSize.Medium,
  [BreedSegmentationIds.MixedLarge]: DogBreedSize.Large,
  [BreedSegmentationIds.Retriever]: DogBreedSize.Large,
  [BreedSegmentationIds.ShepherdDog]: DogBreedSize.Large,
};

const DOG_WET_FOOD_TARGET_WEIGHT_GRAMS_BY_BREED_SIZE: Record<DogBreedSize, number> = {
  [DogBreedSize.Small]: 200,
  [DogBreedSize.Medium]: 400,
  [DogBreedSize.Large]: 800,
};

export function getDogTargetWeight(breed?: BreedSegmentationIds): number {
  const breedSize = breed ? BREED_SIZE_BY_BREED[breed] : undefined;

  return DOG_WET_FOOD_TARGET_WEIGHT_GRAMS_BY_BREED_SIZE[breedSize ?? DogBreedSize.Medium];
}

export function getBundleCompositionDictionary({
  petType,
  breed,
}: {
  petType: typeof PetType.Dog;
  breed: BreedSegmentationIds | undefined;
  isPicky: boolean;
}): BundleCompositionDictionary;
export function getBundleCompositionDictionary({
  petType,
}: {
  petType: typeof PetType.Cat;
  breed?: never;
  isPicky?: never;
}): BundleCompositionDictionary;

export function getBundleCompositionDictionary({
  petType,
  breed,
  isPicky,
}: {
  petType: PetType;
  breed?: BreedSegmentationIds;
  isPicky?: boolean;
}): BundleCompositionDictionary {
  if (petType === PetType.Cat) {
    return BUNDLE_CAT_COMPOSITION_DICTIONARY;
  }

  const isSmallDog = Boolean(breed && SMALL_DOGS_BREEDS.includes(breed));
  let base = BUNDLE_DOG_COMPOSITION_DICTIONARY;

  if (isSmallDog) {
    base = {
      ...base,
      [wetFood]: {
        ...base[wetFood],
        amountPerItem: 1,
      },
    };
  }

  if (isPicky) {
    base = {
      ...base,
      [wetFood]: {
        ...base[wetFood],
        minAmountPerCategory: PICKY_EATER_WET_FOOD_PRODUCTS,
      },
    };
  }

  return base;
}
